import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import SEO from '../../components/SEO';

type Tab = 'story' | 'philosophy';

export default function AboutPage() {
  const [activeTab, setActiveTab] = useState<Tab>('story');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bodhira.online';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/about`,
      url: `${siteUrl}/about`,
      name: 'About – Meet Taylor Schwiering | Bodhira',
      description: 'Meet Taylor Schwiering, certified yoga instructor and founder of Bodhira. Learn about our journey in creating handcrafted botanical wellness products.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'About', item: `${siteUrl}/about` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': `${siteUrl}/about#aboutpage`,
      name: 'About Bodhira – Meet Taylor Schwiering',
      description: 'Learn about Taylor Schwiering, certified yoga instructor and founder of Bodhira, and our mission to bring mindful wellness through botanical products and yoga.',
      url: `${siteUrl}/about`,
      mainEntity: {
        '@type': 'Organization',
        '@id': `${siteUrl}/#business`,
        name: 'Bodhira',
        url: siteUrl,
        description: 'Handcrafted botanical candles, natural ritual sprays, and yin yoga classes rooted in mindful living.',
        foundingDate: '2026',
        founder: {
          '@type': 'Person',
          name: 'Taylor Schwiering',
          jobTitle: 'Founder & Yoga Instructor',
          url: `${siteUrl}/about`,
          sameAs: ['https://instagram.com/bodhira.co'],
          knowsAbout: ['Yin Yoga', 'Mindfulness', 'Botanical Wellness', 'Natural Skincare', 'Essential Oils'],
          hasCredential: [
            {
              '@type': 'EducationalOccupationalCredential',
              name: '200HR Yoga Teacher Training',
              credentialCategory: 'Yoga Certification',
              recognizedBy: { '@type': 'Organization', name: 'Metta Yoga' },
            },
            {
              '@type': 'EducationalOccupationalCredential',
              name: '25HR Yin Yoga Certification',
              credentialCategory: 'Yoga Certification',
              recognizedBy: { '@type': 'Organization', name: 'Jandee Yoga' },
            },
          ],
        },
        sameAs: ['https://instagram.com/bodhira.co'],
        address: {
          '@type': 'PostalAddress',
          addressRegion: 'NJ',
          addressCountry: 'US',
        },
      },
    },
  ];

  return (
    <>
      <SEO
        title="About – Meet Taylor Schwiering | Bodhira"
        description="Meet Taylor Schwiering, certified yoga instructor and founder of Bodhira. Learn about our journey in creating handcrafted botanical wellness products."
        canonical="/about"
        ogTitle="About Bodhira – Our Story & Mission"
        ogDescription="Meet Taylor Schwiering, founder of Bodhira, and learn about our mindful wellness journey."
        keywords="Taylor Schwiering, yoga instructor, Bodhira founder, yin yoga teacher, botanical wellness, New Jersey"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Banner */}
        <div className="relative w-full h-64 overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/23915f37-308f-4fd8-be36-f0359ac7b799.png"
            alt="Bodhira Co Our Story"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/40"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span className="font-sans text-xs tracking-widest uppercase text-cream-100/80 mb-3">Bodhira Co</span>
            <h1 className="font-serif text-5xl text-white font-semibold">Our Story & Philosophy</h1>
            <p className="font-sans text-sm text-cream-100/90 mt-3">Rooted in love, guided by nature, shared with intention.</p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="sticky top-[64px] z-20 bg-white border-b border-sage-100 shadow-sm">
          <div className="max-w-4xl mx-auto px-6 flex items-center gap-2 py-3">
            <div className="flex items-center bg-sage-50 rounded-full px-1 py-1 gap-1">
              <button
                onClick={() => { setActiveTab('story'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`px-6 py-2 rounded-full font-sans text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'story'
                    ? 'bg-sage-500 text-white shadow-sm'
                    : 'text-sage-600 hover:text-sage-700'
                }`}
              >
                Our Story
              </button>
              <button
                onClick={() => { setActiveTab('philosophy'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className={`px-6 py-2 rounded-full font-sans text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'philosophy'
                    ? 'bg-sage-500 text-white shadow-sm'
                    : 'text-sage-600 hover:text-sage-700'
                }`}
              >
                Philosophy
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-20">

          {/* OUR STORY TAB */}
          {activeTab === 'story' && (
            <div className="animate-fade-in">
              {/* Founder Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
                <div className="relative">
                  <div className="rounded-3xl overflow-hidden shadow-xl w-full h-[520px]">
                    <img
                      src="https://static.readdy.ai/image/5489f2d2f138577c10c646e092453b29/b1cbdb4284e64885408ecfccab0f70d0.jpeg"
                      alt="Taylor Schwiering - Founder of Bodhira Co"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-6 -right-6 bg-sage-50 rounded-2xl px-7 py-5 shadow-lg border border-sage-100">
                    <p className="font-serif text-3xl text-sage-600 font-semibold">2026</p>
                    <p className="font-sans text-xs text-sage-500 tracking-widest uppercase mt-1">Founded with Love</p>
                  </div>
                </div>

                <div className="flex flex-col gap-6">
                  <div>
                    <span className="inline-block px-4 py-1.5 bg-sage-400/20 text-sage-600 rounded-full text-xs font-sans font-medium tracking-widest uppercase mb-4">
                      Founder & Creator
                    </span>
                    <h2 className="font-serif text-4xl text-sage-700 leading-tight mb-2">Taylor Schwiering</h2>
                    <div className="w-12 h-0.5 bg-sage-400 rounded-full mb-5"></div>
                  </div>
                  <p className="font-sans text-base text-gray-600 leading-relaxed">
                    Hi, I'm Taylor — and Bodhira Co is my heart poured into a brand. I completed my <strong>200HR YTT with Metta Yoga</strong> + <strong>25HR Yin Certification with Jandee Yoga</strong>, + am  passionate about natural living, and on a mission to share the products I truly love and cherish with the world.
                  </p>
                  <div className="grid grid-cols-3 gap-3 pt-2">
                    {[
                      { icon: 'ri-hand-heart-line', label: 'Handcrafted' },
                      { icon: 'ri-leaf-line', label: 'All Natural' },
                      { icon: 'ri-sparkling-line', label: 'Small Batch' },
                    ].map((item) => (
                      <div key={item.label} className="flex flex-col items-center gap-2 bg-cream-50 rounded-xl py-4 px-2 text-center border border-sage-100/50">
                        <div className="w-9 h-9 flex items-center justify-center bg-sage-400/20 rounded-full">
                          <i className={`${item.icon} text-lg text-sage-600`}></i>
                        </div>
                        <span className="font-sans text-xs text-sage-600 font-medium tracking-wide">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Story Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
                {[
                  {
                    icon: 'ri-heart-line',
                    title: 'Sharing Love',
                    text: 'Everything I create comes from a place of genuine love. I started Bodhira Co because I wanted to share the things that have brought me peace, grounding, and joy — not just sell products. When I make a ritual spray, I\'m thinking about the person who will spray it, the space it will fill, and the moment it will create. That love is in every single thing I make.',
                  },
                  {
                    icon: 'ri-plant-line',
                    title: 'All Natural, Always',
                    text: 'I\'ve always been drawn to natural things — the way a plant can purify a room, the way a single essential oil can shift your entire mood. I\'m passionate about knowing exactly what goes into the products I use, and I want that same transparency for you. Every ingredient in every Bodhira Co product is something I\'ve personally researched, tested, and chosen because I believe in it.',
                  },
                  {
                    icon: 'ri-community-line',
                    title: 'Building Community',
                    text: 'Bodhira Co isn\'t just a shop — it\'s a community. I want this to be a space where people who care about wellness, intention, and natural living can come together. Whether you\'re here for the products, the yoga classes, or just to feel a little more grounded — you belong here. I\'m so grateful you found your way to this little corner of the internet.',
                  },
                ].map((card) => (
                  <div key={card.title} className="bg-cream-50 rounded-2xl p-7 border border-sage-100/60">
                    <div className="w-10 h-10 flex items-center justify-center bg-sage-400/20 rounded-full mb-4">
                      <i className={`${card.icon} text-xl text-sage-600`}></i>
                    </div>
                    <h3 className="font-serif text-xl text-sage-700 mb-3">{card.title}</h3>
                    <p className="font-sans text-sm text-gray-600 leading-relaxed">{card.text}</p>
                  </div>
                ))}
              </div>

              {/* Fun Facts */}
              <div className="bg-sage-50 rounded-3xl p-10 border border-sage-100 mb-14">
                <h3 className="font-serif text-3xl text-sage-700 text-center mb-8">A Few Things About Me</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { icon: 'ri-yoga-line', text: 'Yin yoga teacher & lifelong student' },
                    { icon: 'ri-seedling-line', text: 'Obsessed with all things botanical & herbal' },
                    { icon: 'ri-fire-line', text: 'I love the beach and love to surf!' },
                    { icon: 'ri-sun-line', text: 'Morning ritual devotee — always' },
                    { icon: 'ri-recycle-line', text: 'Committed to low-waste, conscious living' },
                    { icon: 'ri-heart-2-line', text: 'Here to spread love, one product at a time' },
                  ].map((fact) => (
                    <div key={fact.text} className="flex items-center gap-3 bg-white rounded-xl px-4 py-4 border border-sage-100/50">
                      <div className="w-8 h-8 flex items-center justify-center shrink-0">
                        <i className={`${fact.icon} text-lg text-sage-500`}></i>
                      </div>
                      <span className="font-sans text-sm text-gray-600">{fact.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Come Say Hi / Instagram CTA */}
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_e9313e0328a0bfcfcb6676ec2ccd70c0_cb627f14.jpg"
                  alt="Bodhira Co Instagram"
                  className="w-full h-64 object-cover object-top"
                />
                <div className="absolute inset-0 bg-sage-700/55 flex flex-col items-center justify-center text-center px-8">
                  <i className="ri-instagram-line text-4xl text-white/80 mb-3"></i>
                  <h3 className="font-serif text-3xl text-white mb-2">Come Say Hi</h3>
                  <p className="font-sans text-sm text-cream-100/90 mb-5 max-w-md">
                    Follow along on Instagram for behind-the-scenes, new products, and daily doses of botanical goodness.
                  </p>
                  <a
                    href="https://instagram.com/bodhira.co"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-flex items-center gap-2 bg-white text-sage-700 px-7 py-3 rounded-full font-sans text-sm font-semibold hover:bg-cream-50 transition-colors whitespace-nowrap cursor-pointer shadow-md"
                  >
                    <i className="ri-instagram-line text-base"></i>
                    @bodhira.co
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* PHILOSOPHY TAB */}
          {activeTab === 'philosophy' && (
            <div className="animate-fade-in">
              {/* Intro */}
              <div className="text-center max-w-2xl mx-auto mb-16">
                <span className="inline-block px-4 py-1.5 bg-sage-400/20 text-sage-600 rounded-full text-xs font-sans font-medium tracking-widest uppercase mb-5">
                  Our Philosophy
                </span>
                <h2 className="font-serif text-4xl text-sage-700 leading-tight mb-5">
                  Wellness Rooted in<br />
                  <span className="italic text-sage-500">Nature & Intention</span>
                </h2>
                <div className="w-12 h-0.5 bg-sage-400 rounded-full mx-auto mb-6"></div>
                <p className="font-sans text-base text-gray-600 leading-relaxed">
                  At Bodhira Co, we believe that true wellness is simple, accessible, and deeply personal. It doesn't require perfection — just presence.
                </p>
              </div>

              {/* Philosophy Pillars */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
                {[
                  {
                    icon: 'ri-leaf-line',
                    title: 'Natural Living',
                    color: 'bg-sage-400/15',
                    text: 'We believe what you put in and around your body matters. Every Bodhira Co product is crafted with clean, natural ingredients — no synthetic fragrances, no harmful chemicals. Just pure essential oils &nbsp;that you can feel good about.',
                  },
                  {
                    icon: 'ri-focus-3-line',
                    title: 'Intentional Ritual',
                    color: 'bg-amber-100/60',
                    text: 'Ritual is the art of turning the ordinary into the sacred. Misting your space, rolling out your mat — these small acts, done with intention, become anchors of peace in a busy world. We create products that support your rituals.',
                  },
                  {
                    icon: 'ri-heart-pulse-line',
                    title: 'Holistic Wellness',
                    color: 'bg-rose-50',
                    text: 'Wellness is not one-dimensional. It\'s the scent that calms your nervous system, the yoga practice that reconnects you to your body, the community that reminds you you\'re not alone. Bodhira Co exists at the intersection of all of these.',
                  },
                  {
                    icon: 'ri-earth-line',
                    title: 'Sustainability',
                    color: 'bg-green-50',
                    text: 'We care deeply about the planet we live on. Our packaging is minimal and recyclable, and we source ingredients responsibly. Every choice we make is guided by respect for the earth.',
                  },
                ].map((pillar) => (
                  <div key={pillar.title} className="flex gap-5 bg-white rounded-2xl p-7 border border-sage-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className={`w-12 h-12 flex items-center justify-center ${pillar.color} rounded-xl shrink-0`}>
                      <i className={`${pillar.icon} text-2xl text-sage-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-sage-700 mb-2">{pillar.title}</h3>
                      <p className="font-sans text-sm text-gray-600 leading-relaxed">{pillar.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quote Banner */}
              <div className="relative rounded-3xl overflow-hidden mb-20">
                <img
                  src="https://static.readdy.ai/image/5489f2d2f138577c10c646e092453b29/697f9389e4e51d3451418c88ba4231c1.png"
                  alt="Bodhira Co philosophy"
                  className="w-full h-64 object-cover object-top"
                />
                <div className="absolute inset-0 bg-sage-700/60 flex items-center justify-center px-8">
                  <div className="text-center">
                    <i className="ri-double-quotes-l text-4xl text-cream-100/50 mb-3 block"></i>
                    <p className="font-serif text-2xl lg:text-3xl text-white italic leading-relaxed max-w-2xl">
                      "Bodhi means awakening. We're here to help you find yours — one breath, one ritual, one moment at a time."
                    </p>
                  </div>
                </div>
              </div>

              {/* Commitments */}
              <div className="mb-16">
                <h3 className="font-serif text-3xl text-sage-700 text-center mb-10">Our Commitments</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {[
                    { icon: 'ri-recycle-line', title: 'Eco-Conscious', desc: 'Sustainable sourcing, minimal packaging, and recyclable materials in everything we make.' },
                    { icon: 'ri-test-tube-line', title: 'Clean Ingredients', desc: 'No synthetic fragrances, no parabens, no harmful chemicals — ever. Just pure, natural goodness.' },
                    { icon: 'ri-hand-heart-line', title: 'Made with Love', desc: 'Every product is handcrafted in small batches with care, attention, and genuine intention.' },
                  ].map((c) => (
                    <div key={c.title} className="text-center bg-cream-50 rounded-2xl p-8 border border-sage-100/60">
                      <div className="w-14 h-14 flex items-center justify-center bg-sage-400/20 rounded-full mx-auto mb-4">
                        <i className={`${c.icon} text-2xl text-sage-600`}></i>
                      </div>
                      <h4 className="font-serif text-lg text-sage-700 mb-2">{c.title}</h4>
                      <p className="font-sans text-sm text-gray-500 leading-relaxed">{c.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="text-center bg-sage-500 rounded-3xl py-14 px-8">
                <h3 className="font-serif text-3xl text-white mb-3">Ready to Begin Your Ritual?</h3>
                <p className="font-sans text-sm text-cream-100/90 mb-7 max-w-md mx-auto">
                  Explore our botanical sprays — each one made to bring more peace into your everyday life.
                </p>
                <Link
                  to="/shop"
                  className="inline-block bg-white text-sage-600 px-8 py-3 rounded-full font-sans text-sm font-medium hover:bg-cream-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Shop the Collection
                </Link>
              </div>
            </div>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
}
